import React, { useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Check, Info, AlertTriangle, Zap, DollarSign } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import api from '@/utils/api';

const PLAN_PRICES = {
  mensal: {
    basico: 69.90,
    intermediario: 89.90,
    full: 99.90
  },
  trimestral: {
    basico: 209.70,
    intermediario: 269.70,
    full: 299.70
  },
  anual: {
    basico: 687.04,
    intermediario: 863.04,
    full: 959.04
  }
};

const PLAN_FEATURES = {
  basico: [
    'Gestão Financeira (Receitas e Despesas)',
    'Controle de Contratos',
    'Custos Fixos',
    'Fluxo de Caixa',
    'Suporte por E-mail'
  ],
  intermediario: [
    'Tudo do Plano Básico, e:',
    'CRM de Vendas e Leads',
    'Gestão de Projetos e Tarefas',
    'Métricas Básicas',
    'Suporte Prioritário'
  ],
  full: [
    'Acesso Ilimitado a Tudo',
    'Gestão de Equipe (RBAC Avançado)',
    'Controle de Hospedagens',
    'Métricas Financeiras Interativas',
    'Suporte Premium 24/7'
  ]
};

export default function Assinaturas() {
  const { company, setCompany, user } = useAuth();
  const [billingCycle, setBillingCycle] = useState('mensal');
  const [loadingPlan, setLoadingPlan] = useState(null);
  const [pendingPlan, setPendingPlan] = useState(null);
  const [cnpjModal, setCnpjModal] = useState(false);
  const [cnpjInput, setCnpjInput] = useState('');
  const [cnpjLoading, setCnpjLoading] = useState(false);
  const [useReferralBonus, setUseReferralBonus] = useState(false);

  const doCheckout = async (planKey, cnpjOverride) => {
    try {
      setLoadingPlan(planKey);
      const response = await api.post('/company/subscription/checkout', {
        plan: planKey,
        cycle: billingCycle,
        cnpj: cnpjOverride || company?.cnpj || undefined,
        applied_referral_bonus: useReferralBonus
      });
      const { invoiceUrl } = response.data;
      if (invoiceUrl) {
        toast.success('Fatura gerada! Redirecionando para pagamento seguro Asaas...');
        setTimeout(() => { window.location.href = invoiceUrl; }, 1500);
      } else {
        toast.success('Assinatura configurada. O Asaas enviará sua cobrança por e-mail.');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao gerar fatura do plano.');
    } finally {
      setLoadingPlan(null);
    }
  };

  const handleUpgrade = async (planKey) => {
    if (user.role !== 'admin' && user.role !== 'super_admin') {
      toast.error('Apenas administradores podem alterar o plano da empresa.');
      return;
    }
    // Se não tem CNPJ, abre modal antes de prosseguir
    if (!company?.cnpj) {
      setPendingPlan(planKey);
      setCnpjModal(true);
      return;
    }
    await doCheckout(planKey);
  };

  const isReferralEligible = company?.referred_by_user_id && !company?.has_applied_referral_discount;
  const REFERRAL_DISCOUNT = 0.10;

  const getPriceData = (planKey) => {
    const originalPrice = PLAN_PRICES[billingCycle][planKey];
    if (isReferralEligible) {
      return {
        original: originalPrice,
        final: originalPrice * (1-REFERRAL_DISCOUNT),
        hasDiscount: true
      };
    }
    return {
      original: originalPrice,
      final: originalPrice,
      hasDiscount: false
    };
  };

  const formatBRL = (val) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(val);

  const [couponCode, setCouponCode] = useState('');
  const [applyingCoupon, setApplyingCoupon] = useState(false);
  const { refreshAuth } = useAuth();

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) return;
    setApplyingCoupon(true);
    try {
      const response = await api.post('/company/apply-coupon', { referral_code: couponCode });
      toast.success(response.data.message);
      await refreshAuth(); // Atualiza os dados da empresa no contexto para liberar o desconto
      setCouponCode('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao aplicar cupom');
    } finally {
      setApplyingCoupon(false);
    }
  };

  const handleCnpjSubmit = async (e) => {
    e.preventDefault();
    const raw = cnpjInput.replace(/\D/g, '');
    if (raw.length < 11) {
      toast.error('Informe um CPF (11 dígitos) ou CNPJ (14 dígitos) válido.');
      return;
    }
    setCnpjLoading(true);
    try {
      // O CNPJ agora é salvo diretamente pelo endpoint de checkout no backend
      // para evitar chamadas redundantes e erros de validação 422.
      setCnpjModal(false);
      await doCheckout(pendingPlan, cnpjInput);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao processar dados da empresa.');
    } finally {
      setCnpjLoading(false);
    }
  };

  const isCurrentPlan = (planKey) => company?.plan === planKey;

  return (
    <div className="space-y-8">

      {/* Modal CNPJ obrigatório */}
      <Dialog open={cnpjModal} onOpenChange={setCnpjModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              CPF / CNPJ obrigatório
            </DialogTitle>
            <DialogDescription>
              O Asaas exige CPF ou CNPJ para emitir cobranças. Preencha abaixo para continuar com a assinatura.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCnpjSubmit} className="space-y-4 mt-2">
            <div>
              <Label>CPF ou CNPJ da Empresa</Label>
              <Input 
                placeholder="00.000.000/0001-00 ou 000.000.000-00"
                value={cnpjInput}
                onChange={e => setCnpjInput(e.target.value)}
                required
              />
            </div>
            <div className="flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={() => setCnpjModal(false)}>Cancelar</Button>
              <Button type="submit" disabled={cnpjLoading}>
                {cnpjLoading ? 'Salvando...' : 'Salvar e Continuar'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Alerta de Desconto de Indicação (10%) */}
      {company?.referred_by_user_id && !company?.has_applied_referral_discount && (
        <div className="bg-emerald-50 border border-emerald-100 p-4 rounded-xl flex items-center gap-4 text-emerald-800 text-sm animate-pulse mb-6">
          <Zap className="h-5 w-5 text-emerald-500" />
          <div>
            <p className="font-bold text-base">Boas-vindas! Você ganhou 10% de Desconto extra!</p>
            <p className="opacity-90">Por ter vindo através de uma indicação, sua primeira compra em qualquer plano terá 10% de desconto automático.</p>
          </div>
        </div>
      )}

      <div>
        <h1 className="text-3xl font-heading font-bold text-slate-900">Planos e Assinaturas (v2)</h1>
        <p className="text-slate-500 mt-2">Personalize a escala do sistema de acordo com a necessidade da sua empresa.</p>
      </div>

      <div className="flex justify-center mb-10">
        <div className="bg-slate-100 p-1 rounded-xl shadow-inner inline-flex">
          {['mensal', 'trimestral', 'anual'].map((cycle) => (
            <button
              key={cycle}
              onClick={() => setBillingCycle(cycle)}
              className={`px-6 py-2 rounded-lg text-sm font-medium transition-all ${
                billingCycle === cycle 
                  ? 'bg-white text-primary shadow-sm' 
                  : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              {cycle.charAt(0).toUpperCase() + cycle.slice(1)}
              {cycle === 'anual' && <span className="ml-2 text-[10px] bg-green-100 text-green-700 px-2 py-0.5 rounded-full">-20%</span>}
            </button>
          ))}
        </div>
      </div>
      
      {/* Seção de Cupom Manual */}
      {!isReferralEligible && !company?.has_applied_referral_discount && (
        <div className="flex justify-center mb-10">
          <div className="bg-white border border-slate-200 p-2 pl-4 rounded-xl shadow-sm flex items-center gap-2 max-w-sm w-full">
            <Zap className="h-4 w-4 text-primary" />
            <input 
              type="text" 
              placeholder="Possui um Cupom?" 
              value={couponCode}
              onChange={e => setCouponCode(e.target.value)}
              className="flex-grow bg-transparent border-none outline-none text-sm font-medium"
            />
            <Button 
              size="sm" 
              className="h-8 rounded-lg"
              disabled={applyingCoupon || !couponCode.trim()}
              onClick={handleApplyCoupon}
            >
              {applyingCoupon ? '...' : 'Aplicar'}
            </Button>
          </div>
        </div>
      )}

      {/* Opção de Usar Saldo de Afiliado */}
      {user?.referral_balance > 0 && (
        <div className="flex justify-center mb-10">
          <div className="bg-white border-2 border-emerald-500/20 p-4 rounded-2xl shadow-sm max-w-md w-full flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 bg-emerald-100 rounded-xl flex items-center justify-center text-emerald-600">
                <DollarSign className="h-5 w-5" />
              </div>
              <div>
                <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">Saldo de Indicação</p>
                <p className="text-lg font-bold text-emerald-600">
                  {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(user.referral_balance)}
                </p>
              </div>
            </div>
            <div className="flex flex-col items-end gap-1">
              <Label className="text-[10px] font-bold text-slate-400 uppercase">Usar no checkout?</Label>
              <button 
                onClick={() => setUseReferralBonus(!useReferralBonus)}
                className={`w-12 h-6 rounded-full transition-colors relative ${useReferralBonus ? 'bg-emerald-50' : 'bg-slate-200'}`}
              >
                <div className={`absolute top-1 w-4 h-4 rounded-full shadow-sm transition-all ${useReferralBonus ? 'left-7 bg-emerald-500' : 'left-1 bg-white'}`} />
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {/* BASICO */}
        <div className={`bg-white rounded-2xl border ${isCurrentPlan('basico') ? 'border-primary ring-2 ring-primary/20' : 'border-slate-200'} shadow-soft p-8 relative flex flex-col`}>
          {isCurrentPlan('basico') && <span className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-primary text-white text-xs font-bold px-3 py-1 rounded-full border-4 border-white">PLANO ATUAL</span>}
          <h3 className="text-xl font-bold text-slate-900 mb-2">Básico</h3>
          <p className="text-slate-500 text-sm mb-6">Controle financeiro e contratos para começar forte.</p>
          <div className="mb-6 flex flex-col">
            {getPriceData('basico').hasDiscount && (
              <div className="flex flex-col mb-1 gap-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-400 line-through">{formatBRL(getPriceData('basico').original)}</span>
                  <span className={cn(
                    "text-[10px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider",
                    billingCycle === 'anual' ? "bg-amber-100 text-amber-600 animate-pulse" : "bg-emerald-100 text-emerald-600"
                  )}>
                    {billingCycle === 'anual' ? '30% OFF (20% + 10%)' : '10% OFF EXCLUSIVO'}
                  </span>
                </div>
                {billingCycle === 'anual' && (
                  <span className="text-[10px] font-bold text-emerald-600 uppercase">Economia Máxima Ativada!</span>
                )}
              </div>
            )}
            <div className="flex items-baseline gap-1">
              <span className={cn("text-4xl font-bold", getPriceData('basico').hasDiscount ? "text-emerald-600" : "text-slate-900")}>
                {formatBRL(getPriceData('basico').final)}
              </span>
              <span className="text-slate-500 text-sm">/{billingCycle === 'mensal' ? 'mês' : billingCycle}</span>
            </div>
          </div>
          <ul className="space-y-4 mb-8 flex-grow">
            {PLAN_FEATURES.basico.map((feature, idx) => (
              <li key={idx} className="flex items-start text-sm text-slate-600">
                <Check className="h-5 w-5 text-emerald-500 mr-3 flex-shrink-0" />
                <span>{feature}</span>
              </li>
            ))}
          </ul>
          <Button 
            className={`w-full ${isCurrentPlan('basico') ? 'bg-slate-100 text-slate-500 hover:bg-slate-200' : ''}`}
            variant={isCurrentPlan('basico') ? 'outline' : 'default'}
            disabled={isCurrentPlan('basico') || loadingPlan === 'basico'}
            onClick={() => handleUpgrade('basico')}
          >
            {isCurrentPlan('basico') ? 'Plano Ativo' : 'Escolher Básico'}
          </Button>
        </div>

        {/* INTERMEDIARIO */}
        <div className={`bg-white rounded-2xl border ${isCurrentPlan('intermediario') ? 'border-primary ring-2 ring-primary/20' : 'border-slate-200'} shadow-soft p-8 relative flex flex-col`}>
          {isCurrentPlan('intermediario') && <span className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-primary text-white text-xs font-bold px-3 py-1 rounded-full border-4 border-white">PLANO ATUAL</span>}
          <h3 className="text-xl font-bold text-primary mb-2">Intermediário</h3>
          <p className="text-slate-500 text-sm mb-6">Acelere com CRM e controle de tarefas integrado.</p>
          <div className="mb-6 flex flex-col">
            {getPriceData('intermediario').hasDiscount && (
              <div className="flex flex-col mb-1 gap-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-400 line-through">{formatBRL(getPriceData('intermediario').original)}</span>
                  <span className={cn(
                    "text-[10px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider",
                    billingCycle === 'anual' ? "bg-amber-100 text-amber-600 animate-pulse" : "bg-primary/10 text-primary"
                  )}>
                    {billingCycle === 'anual' ? '30% OFF (20% + 10%)' : '10% OFF EXCLUSIVO'}
                  </span>
                </div>
                {billingCycle === 'anual' && (
                  <span className="text-[10px] font-bold text-primary uppercase">Descontos Acumulados!</span>
                )}
              </div>
            )}
            <div className="flex items-baseline gap-1">
              <span className="text-4xl font-bold text-primary">
                {formatBRL(getPriceData('intermediario').final)}
              </span>
              <span className="text-slate-500 text-sm">/{billingCycle === 'mensal' ? 'mês' : billingCycle}</span>
            </div>
          </div>
          <ul className="space-y-4 mb-8 flex-grow">
            {PLAN_FEATURES.intermediario.map((feature, idx) => (
              <li key={idx} className="flex items-start text-sm text-slate-600">
                <Check className="h-5 w-5 text-primary mr-3 flex-shrink-0" />
                <span className={idx === 0 ? 'font-medium' : ''}>{feature}</span>
              </li>
            ))}
          </ul>
          <Button 
            className={`w-full ${isCurrentPlan('intermediario') ? 'bg-slate-100 text-slate-500 hover:bg-slate-200' : 'bg-primary hover:bg-primary/90'}`}
            variant={isCurrentPlan('intermediario') ? 'outline' : 'default'}
            disabled={isCurrentPlan('intermediario') || loadingPlan === 'intermediario'}
            onClick={() => handleUpgrade('intermediario')}
          >
            {isCurrentPlan('intermediario') ? 'Plano Ativo' : 'Fazer Upgrade'}
          </Button>
        </div>

        {/* FULL */}
        <div className={`bg-slate-900 rounded-2xl border ${isCurrentPlan('full') ? 'border-primary ring-2 ring-primary/20' : 'border-slate-800'} shadow-xl p-8 relative flex flex-col`}>
          {isCurrentPlan('full') && <span className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-primary text-white text-xs font-bold px-3 py-1 rounded-full border-4 border-slate-900">PLANO ATUAL</span>}
          <h3 className="text-xl font-bold text-white mb-2">Acesso Full</h3>
          <p className="text-slate-400 text-sm mb-6">Escala máxima para agências e estúdios digitais.</p>
          <div className="mb-6 flex flex-col">
            {getPriceData('full').hasDiscount && (
              <div className="flex flex-col mb-1 gap-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-500 line-through">{formatBRL(getPriceData('full').original)}</span>
                  <span className={cn(
                    "text-[10px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider text-white",
                    billingCycle === 'anual' ? "bg-amber-500 animate-pulse" : "bg-primary"
                  )}>
                     {billingCycle === 'anual' ? '30% OFF (20% + 10%)' : '10% OFF EXCLUSIVO'}
                  </span>
                </div>
                {billingCycle === 'anual' && (
                  <span className="text-[10px] font-bold text-amber-400 uppercase">SUPER DESCONTO: ANUAL + CUPOM</span>
                )}
              </div>
            )}
            <div className="flex items-baseline gap-1">
              <span className="text-4xl font-bold text-white">
                {formatBRL(getPriceData('full').final)}
              </span>
              <span className="text-slate-400 text-sm">/{billingCycle === 'mensal' ? 'mês' : billingCycle}</span>
            </div>
          </div>
          <ul className="space-y-4 mb-8 flex-grow">
            {PLAN_FEATURES.full.map((feature, idx) => (
              <li key={idx} className="flex items-start text-sm text-slate-300">
                <Check className="h-5 w-5 text-primary mr-3 flex-shrink-0" />
                <span className={idx === 0 ? 'font-medium text-white' : ''}>{feature}</span>
              </li>
            ))}
          </ul>
          <Button 
            className={`w-full ${isCurrentPlan('full') ? 'bg-slate-800 text-white hover:bg-slate-700' : 'bg-primary hover:bg-primary/90 text-white border-0'}`}
            variant={isCurrentPlan('full') ? 'outline' : 'default'}
            disabled={isCurrentPlan('full') || loadingPlan === 'full'}
            onClick={() => handleUpgrade('full')}
          >
            {isCurrentPlan('full') ? 'Plano Ativo' : 'Tornar-se Full'}
          </Button>
        </div>
      </div>
      
      <div className="mt-8 bg-blue-50 text-blue-800 p-4 rounded-xl flex items-start gap-4 text-sm border border-blue-100">
         <Info className="h-5 w-5 flex-shrink-0 text-blue-500 mt-0.5" />
         <p>
           O upgrade do plano emite uma fatura oficial pelo portal de pagamentos. A sua conta e seus Colaboradores terão as funcionalidades destrancadas instantaneamente logo após a aprovação (Pix, Boleto ou Cartão).
         </p>
      </div>

    </div>
  );
}
